// components/FullMenu/FullMenu.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import "./full-menu.css";
import SelectedMenuItem from "../SelectedMenuItem/SelectedMenuItem";
import { API_BASE_URL } from "../../config";

const PAGE_SIZE = 9;

const FullMenu = ({ selectedCategory, items, restaurant }) => {
  const [activeCategory, setActiveCategory] = useState(selectedCategory);
  const [selectedItem, setSelectedItem] = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");
  // Categories are admin-managed now (see the Categories admin page) - a
  // hidden one (e.g. "Mango" during the off-season) just doesn't appear
  // as a tab here, rather than a code change + redeploy to remove it.
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/category`)
      .then((res) => setCategories(res.data.categories))
      .catch((err) => console.error("Error fetching categories:", err));
  }, []);

  // "Popular" is a real tab alongside the meal-time categories, but isn't
  // one of them - it's a staff-curated highlight flag on the item
  // (item.popular), not a category with its own price, so it's handled
  // separately here rather than living in the admin-managed Category
  // list. Only shown when this restaurant actually has a popular item,
  // so it doesn't sit there as a permanently-empty tab.
  const hasPopular = items.some((item) => item.popular);
  const allCategories = ["All", ...(hasPopular ? ["Popular"] : []), ...categories.map((c) => c.slug)];
  const categoryLabel = (slug) =>
    categories.find((c) => c.slug === slug)?.name || slug.replace(/_/g, " ");

  // "All" and "Popular" aren't real categories and are never time-gated.
  // A real category not in this list at all (data predating the schedule
  // feature) is treated the same as isOpenNow - no restriction.
  const activeCategoryDoc = categories.find((c) => c.slug === activeCategory);
  const isActiveCategoryClosed = Boolean(activeCategoryDoc) && !activeCategoryDoc.isOpenNow;

  const formatTime12h = (time24) => {
    const [h, m] = time24.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
  };

  // Update the active category when the selectedCategory prop changes, and
  // reset pagination back to the first page - staying on "page 3" of a
  // category the user just switched away from would just show an empty grid.
  useEffect(() => {
    setActiveCategory(selectedCategory);
    setVisibleCount(PAGE_SIZE);
  }, [selectedCategory]);

  // 🔥 Track category views when category changes
  useEffect(() => {
    if (window.fbq) {
      window.fbq("track", "ViewContent", {
        content_name: `${activeCategory} Menu`,
        content_category: activeCategory,
        content_type: "product_group",
      });
      console.log("📂 Category View tracked:", activeCategory);
    }
  }, [activeCategory]);

  const handleFilterClick = (category) => {
    setActiveCategory(category);
    setVisibleCount(PAGE_SIZE); // same reset as switching category via a prop
  };

  // Filter menu items based on the active category and search text -
  // client-side, not a server search endpoint, since a single
  // restaurant's menu (tens of items) is small enough that filtering
  // what's already loaded is simpler and just as fast.
  const query = search.trim().toLowerCase();
  const filteredMenu = items.filter((item) => {
    const matchesCategory =
      activeCategory === "All" ||
      (activeCategory === "Popular"
        ? item.popular
        : item.categoryPricing.some((cp) => cp.category === activeCategory));
    const matchesSearch =
      !query ||
      item.title.toLowerCase().includes(query) ||
      item.desc.toLowerCase().includes(query);

    return matchesCategory && matchesSearch && item.availability;
  });

  // Price shown/charged depends on which meal-time tab is active - the
  // same dish can be priced differently for lunch vs dinner vs late-night.
  // Under "All" or "Popular" (neither is a real category with its own
  // price), show the cheapest listed price and let the modal default to
  // that category too (see SelectedMenuItem's own fallback).
  const priceFor = (item) =>
    item.categoryPricing.find((cp) => cp.category === activeCategory)?.price ??
    Math.min(...item.categoryPricing.map((cp) => cp.price));
  const categoryLabelFor = (item) =>
    activeCategory !== "All" &&
    activeCategory !== "Popular" &&
    item.categoryPricing.some((cp) => cp.category === activeCategory)
      ? activeCategory
      : item.categoryPricing[0].category;

  const visibleMenu = filteredMenu.slice(0, visibleCount);
  const hasMore = visibleCount < filteredMenu.length;

  return (
    <section className="full-menu" id="full-menu">
      <div className="full-menu-container">
        <span className="eyebrow">Fresh Today</span>
        <h2 className="full-menu-title">Our Menu</h2>

        <input
          type="text"
          placeholder="Search this menu…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setVisibleCount(PAGE_SIZE);
          }}
          className="full-menu-search"
        />

        {/* Category Filters */}
        <div className="menu-filters">
          {allCategories.map((category) => {
            const categoryDoc = categories.find((c) => c.slug === category);
            const closed = Boolean(categoryDoc) && !categoryDoc.isOpenNow;
            return (
              <button
                key={category}
                className={`filter-btn ${activeCategory === category ? "active" : ""} ${
                  closed ? "filter-btn-closed" : ""
                }`}
                onClick={() => handleFilterClick(category)}
              >
                {category === "All" ? "ALL" : categoryLabel(category).toUpperCase()}
                {closed && " (Closed)"}
              </button>
            );
          })}
        </div>

        {/* A category can be scheduled to specific hours (Bangladesh
            time, see the Categories admin page) - outside them, this
            replaces the item grid entirely rather than just showing
            nothing, so it's clear the category exists and when it'll be
            back rather than looking broken or empty. */}
        {isActiveCategoryClosed ? (
          <p className="full-menu-closed-message">
            {activeCategoryDoc.name} is closed right now.
            {activeCategoryDoc.startTime && activeCategoryDoc.endTime && (
              <>
                {" "}Available {formatTime12h(activeCategoryDoc.startTime)} – {formatTime12h(activeCategoryDoc.endTime)} (Bangladesh time).
              </>
            )}
          </p>
        ) : (
        <>
        {/* Menu Items */}
        <div className="full-menu-content">
          {visibleMenu.map((item) => (
            <div key={item._id} className="menu-item">
              <img
                src={item.imageUrl[0]}
                alt={item.title}
                className="menu-item-img"
              />
              <h3 className="menu-item-title">{item.title}</h3>
              <p className="menu-item-description">{item.desc}</p>
              <p className="font-mono text-xs tracking-wide text-accent font-[600] pt-4">
                {categoryLabel(categoryLabelFor(item)).toUpperCase()}
              </p>
              <div className="menu-item-price-order">
                <span className="menu-item-price">৳{priceFor(item)}</span>
                <button
                  className="menu-item-order-btn btn btn-primary"
                  onClick={() =>
                    setSelectedItem({
                      ...item,
                      selectedCategory: categoryLabelFor(item),
                      restaurant,
                    })
                  }
                >
                  Add to Cart
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredMenu.length === 0 && (
          <p className="text-center text-gray-500 py-8">
            {query ? `No items match "${search}".` : "No items in this category yet."}
          </p>
        )}

        {hasMore && (
          <div className="full-menu-load-more">
            <button
              className="btn btn-outline"
              onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
            >
              Load more
            </button>
          </div>
        )}
        </>
        )}
      </div>

      {/* Selected Item Modal */}
      {selectedItem && (
        <SelectedMenuItem
          menuItem={selectedItem}
          closeModal={() => setSelectedItem(null)}
        />
      )}
    </section>
  );
};

export default FullMenu;
